import { token } from '@/infrastructure/container';
import { redactKeys } from '@/domain/joinLink';
import { type ILogger } from './Logger';

export interface TraceEvent {
  readonly seq: number;
  readonly name: string;
  readonly fields: Readonly<Record<string, unknown>>;
}

export interface ITraceSink {
  write(event: TraceEvent): void;
}

// Trace names are free strings, unlike metric names: a trace line is narrative, not a series.
export interface ITrace {
  event(name: string, fields?: Record<string, unknown>): void;
}

export const TOKEN_ITrace = token<ITrace>('ITrace');

export class Trace implements ITrace {
  private sinks: ITraceSink[];
  private seq = 0;

  constructor(...sinks: ITraceSink[]) {
    this.sinks = sinks;
  }

  event(name: string, fields: Record<string, unknown> = {}): void {
    const event: TraceEvent = { seq: this.seq++, name, fields: scrub(fields) };
    for (const sink of this.sinks) sink.write(event);
  }

  // Off ITrace, like AppBus.handlerCount: only the composition root wires sinks (main.tsx arms
  // the ring buffer at boot). Injected consumers can emit, never re-plumb.
  addSink(sink: ITraceSink): void {
    this.sinks.push(sink);
  }
}

// Every string that reaches a sink goes through here first. BusNarrator narrates each bus payload
// VERBATIM, and URL_CHANGED's payload is the whole address bar, so on a scanned join the trace
// timeline — and with it the ring buffer main.tsx hangs on window.__telemetry, and every Playwright
// trace attachment saved to test-results/ — holds two live bearer keys. Redacting at this one
// choke point covers the narrator and any future traced URL without every caller having to
// remember (docs/access.md: a bearer key never reaches a log line).
//
// Depth-bounded rather than freely recursive: this runs inside the publish path, and a bound is
// also what makes a cyclic payload impossible to hang on. Trace fields are small and flat in
// practice; anything past the bound is passed through untouched rather than walked.
const SCRUB_DEPTH = 4;

function scrub(fields: Record<string, unknown>): Record<string, unknown> {
  return scrubValue(fields, 0) as Record<string, unknown>;
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return redactKeys(value);
  if (depth >= SCRUB_DEPTH || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => scrubValue(item, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) out[key] = scrubValue(item, depth + 1);
  return out;
}

// Hardened: a sink formats inside the publish path (narrator -> trace -> sink), so a payload
// JSON.stringify chokes on must degrade to a marker line, never throw. Telemetry that throws
// breaks the app it observes.
function formatLine(event: TraceEvent): string {
  let fields: string;
  try {
    fields = JSON.stringify(event.fields, (_key, value: unknown) => {
      if (typeof value === 'function') return '[function]';
      if (typeof value === 'bigint') return String(value);
      return value;
    });
  } catch {
    fields = '[unserializable fields]';
  }
  return `${event.seq} ${event.name} ${fields}`;
}

/**
 * Test sink: a typed timeline for assertions, plus one logger.info line per event so a red
 * Vitest run dumps the whole story. Green runs stay quiet.
 */
export class RecordingTraceSink implements ITraceSink {
  readonly timeline: TraceEvent[] = [];

  constructor(private logger: ILogger) {}

  write(event: TraceEvent): void {
    this.timeline.push(event);
    this.logger.info('trace', formatLine(event));
  }

  names(): string[] {
    return this.timeline.map((e) => e.name);
  }

  dump(): string {
    return this.timeline.map(formatLine).join('\n');
  }
}

/** e2e sink: the last `capacity` events, exposed on window.__telemetry by main.tsx (gated). */
export class RingBufferTraceSink implements ITraceSink {
  private buffer: TraceEvent[] = [];

  constructor(private capacity = 500) {}

  write(event: TraceEvent): void {
    this.buffer.push(event);
    if (this.buffer.length > this.capacity) this.buffer.shift();
  }

  snapshot(): TraceEvent[] {
    return [...this.buffer];
  }

  dump(): string {
    return this.buffer.map(formatLine).join('\n');
  }
}

/** Browser sink: rides the logger so its level knob applies. Quiet at dev's info level. */
export class ConsoleTraceSink implements ITraceSink {
  constructor(private logger: ILogger) {}

  write(event: TraceEvent): void {
    this.logger.debug('trace', formatLine(event));
  }
}
